/** The Assist bar (docs/features/sketch.md § 7): a slim glass group on the map, in the tool rail's style, with one
 * button per helper: a small brand spark, a verb, and under it the included uses left or, once they are used, the
 * Lux price; once bought, what the use still covers ("Already included for this design", "Included until you leave
 * this page"). Plus the roof hints review (accept per face or all) and the contextual nudges. Presentational: the
 * editor (SketchEditor.tsx) runs the helpers; the wording comes from assist.ts. Styles: sketch.css (`.sk-assist…`).
 */
import { Box, Check, Mountain, ShieldCheck, Sparkles, X } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { LuxGlyph } from "../components/LuxIcon";
import { FACE_HUE } from "./SketchCanvas";
import { featureLabel, featureView, hintRows, hintsCost, verbOf, type Feature, type Hints } from "./assist.ts";
import type { Sketch } from "./skeleton.ts";
import { faceName } from "./text.ts";
import { useSketchText } from "./useText.ts";

const ICON: Record<string, ReactNode> = { roof_hints: <Mountain size={17} />, surroundings: <Box size={17} /> };

export interface AssistBarProps {
  features: Feature[] | null;
  balance: number | null;
  /** The helper running now (its button shows progress). */
  busy: string | null;
  /** 3D surroundings are shown now. */
  surroundingsOn: boolean;
  /** This page has a 3D surroundings session (shown or hidden): clicks never charge again. */
  surroundingsSession?: boolean;
  readOnly: boolean;
  onRun: (key: string, anchor: HTMLElement) => void;
  /** "Close gaps" (free, in the browser): the gaps the roof check finds now, whether it is open. */
  roofCheck?: { gaps: number; on: boolean; onRun: () => void } | null;
}

export function AssistBar({ features, balance, busy, surroundingsOn, surroundingsSession = false, readOnly, onRun, roofCheck }: AssistBarProps) {
  const tx = useSketchText();
  if (!features && !roofCheck) return null;
  return (
    <div className="sk-assist sk-glass" role="toolbar" aria-label={tx.t("sketch.assist.label")}>
      <span className="sk-assist-label" aria-hidden>
        <Sparkles size={12} /> {tx.t("sketch.assist.label")}
      </span>
      {roofCheck && (
        <button
          type="button"
          className={`sk-assist-btn is-free sk-roofcheck-btn${roofCheck.on ? " is-on" : ""}${roofCheck.gaps ? " has-gaps" : ""}`}
          aria-pressed={roofCheck.on}
          onClick={roofCheck.onRun}
        >
          <span className="sk-assist-icon">
            <ShieldCheck size={17} />
            {roofCheck.gaps > 0 && <i className="sk-roofcheck-count" aria-hidden>{roofCheck.gaps}</i>}
          </span>
          <span className="sk-assist-text">
            <b>
              {tx.t("sketch.assist.closeGaps")}
              <span className="sk-assist-verb"> · {tx.t(roofCheck.on ? "sketch.assist.on" : "sketch.assist.check")}</span>
            </b>
            <small>{roofCheck.on ? tx.t("sketch.assist.clickToHide") : roofCheck.gaps ? tx.t("sketch.assist.gapsFree", { count: roofCheck.gaps }) : tx.t("sketch.assist.roofClosedFree")}</small>
          </span>
          <span className="sk-assist-tip" role="tooltip">{tx.t("sketch.assist.closeGapsTip")}</span>
        </button>
      )}
      {(features ?? []).map((f) => {
        const view = featureView(tx, f, balance, { sessionActive: f.key === "surroundings" && surroundingsSession });
        const running = busy === f.key;
        const on = f.key === "surroundings" && surroundingsOn;
        const disabled = readOnly || running || (!view.clickable && !on);
        return (
          <button
            key={f.key}
            type="button"
            className={`sk-assist-btn is-${view.kind}${on ? " is-on" : ""}${running ? " is-running" : ""}`}
            aria-pressed={f.key === "surroundings" ? on : undefined}
            disabled={disabled}
            onClick={(e) => onRun(f.key, e.currentTarget)}
          >
            <span className="sk-assist-icon">
              {ICON[f.key]}
              <i className="sk-assist-spark" aria-hidden />
            </span>
            <span className="sk-assist-text">
              <b>
                {featureLabel(tx, f)}
                <span className="sk-assist-verb"> · {on ? tx.t("sketch.assist.on") : verbOf(tx, f.key)}</span>
              </b>
              <small>
                {running ? (
                  tx.t(f.key === "roof_hints" ? "sketch.assist.readingRoofData" : "sketch.assist.openingSurroundings")
                ) : view.kind === "design" || view.kind === "session" ? (
                  <>
                    <Check size={11} aria-hidden /> {view.subline}
                  </>
                ) : on ? (
                  tx.t("sketch.assist.clickToHide")
                ) : view.kind === "paid" || view.kind === "out" ? (
                  <>
                    <LuxGlyph size={11} /> {view.subline}
                  </>
                ) : (
                  <>
                    {view.kind === "low" && <i className="sk-assist-dot" aria-hidden />}
                    {view.subline}
                  </>
                )}
              </small>
            </span>
            {running && <span className="sk-assist-progress" aria-hidden />}
            <span className="sk-assist-tip" role="tooltip">{on ? tx.t("sketch.assist.clickToHideTip", { tooltip: view.tooltip }) : view.tooltip}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The roof hints, face by face: what would change, accept or reject each, or accept all the suggestions. */
export function HintsPanel({
  hints,
  sketch,
  decided,
  onAccept,
  onReject,
  onAcceptAll,
  onClose,
}: {
  hints: Hints;
  sketch: Sketch;
  decided: Record<string, "accepted" | "rejected">;
  onAccept: (faceId: string) => void;
  onReject: (faceId: string) => void;
  onAcceptAll: () => void;
  onClose: () => void;
}) {
  const tx = useSketchText();
  const index = new Map(sketch.faces.map((f, i) => [f.id, i]));
  const labels = Object.fromEntries(sketch.faces.map((f, i) => [f.id, faceName(tx, f.label, i)]));
  const rows = hintRows(tx, hints, sketch, labels);
  const open = rows.filter((r) => r.tone === "suggested" && !decided[r.hint.face_id]);
  const counts = {
    suggested: rows.filter((r) => r.tone === "suggested").length,
    ok: rows.filter((r) => r.tone === "ok").length,
    locked: rows.filter((r) => r.tone === "locked").length,
    none: rows.filter((r) => r.tone === "none").length,
  };
  const summary = [
    counts.suggested && tx.t("sketch.hints.toUpdate", { count: counts.suggested }),
    counts.ok && tx.t("sketch.hints.alreadyMatch", { count: counts.ok }),
    counts.locked && tx.t("sketch.hints.setByYou", { count: counts.locked }),
    counts.none && tx.t("sketch.hints.withoutData", { count: counts.none }),
  ].filter((x): x is string => !!x);
  const month = hints.imagery_date && /^\d{4}-\d{2}/.test(hints.imagery_date) ? tx.date(new Date(Number(hints.imagery_date.slice(0, 4)), Number(hints.imagery_date.slice(5, 7)) - 1, 15), { year: "numeric", month: "short" }) : null;
  return (
    <section className="sk-hints sk-glass" aria-label={tx.t("sketch.action.roofHints")}>
      <header className="sk-hints-head">
        <span className="sk-hints-mark"><Mountain size={16} /></span>
        <div>
          <h3>{tx.t("sketch.action.roofHints")}</h3>
          <p>
            {month ? tx.t("sketch.hints.sourceDated", { month, cost: hintsCost(tx, hints.use) }) : tx.t("sketch.hints.source", { cost: hintsCost(tx, hints.use) })}
          </p>
        </div>
        <button type="button" className="sk-icon-action" onClick={onClose} aria-label={tx.t("sketch.hints.close")} title={tx.t("sketch.common.close")}>
          <X size={16} />
        </button>
      </header>
      <p className="sk-hints-summary">{summary.length ? tx.list(summary) : tx.t("sketch.hints.nothing")}</p>
      <ul className="sk-hints-list">
        {rows.map(({ hint, label, change, tone }) => {
          const state = decided[hint.face_id];
          const i = index.get(hint.face_id) ?? 0;
          return (
            <li key={hint.face_id} className={`sk-hint-row is-${tone}${state ? ` is-${state}` : ""}`}>
              <span className="sk-face-swatch is-small" style={{ "--sk-face-hue": FACE_HUE[i % FACE_HUE.length] } as CSSProperties}>{i + 1}</span>
              <span className="sk-hint-text">
                <b>{label}</b>
                <span>{change}</span>
                {hint.note && <span className="sk-hint-note">{hint.note}</span>}
              </span>
              {tone === "suggested" &&
                (state ? (
                  <span className="sk-hint-state">{state === "accepted" ? <><Check size={13} /> {tx.t("sketch.hints.applied")}</> : tx.t("sketch.hints.keptYours")}</span>
                ) : (
                  <span className="sk-hint-actions">
                    <button type="button" className="sk-hint-btn is-accept" onClick={() => onAccept(hint.face_id)} aria-label={tx.t("sketch.hints.acceptFor", { face: label })} title={tx.t("sketch.hints.accept")}>
                      <Check size={15} />
                    </button>
                    <button type="button" className="sk-hint-btn" onClick={() => onReject(hint.face_id)} aria-label={tx.t("sketch.hints.rejectFor", { face: label })} title={tx.t("sketch.hints.keepMine")}>
                      <X size={15} />
                    </button>
                  </span>
                ))}
            </li>
          );
        })}
      </ul>
      {hints.unmatched_segments.length > 0 && (
        <p className="sk-hints-extra">
          {tx.t("sketch.hints.unmatched", { count: hints.unmatched_segments.length })}
        </p>
      )}
      <footer className="sk-hints-foot">
        <button type="button" className="sk-action is-primary is-grow" disabled={!open.length} onClick={onAcceptAll}>
          <Check size={15} /> {tx.t("sketch.hints.acceptAll", { count: open.length })}
        </button>
        <button type="button" className="sk-action" onClick={onClose}>
          {tx.t("sketch.common.done")}
        </button>
      </footer>
      <p className="sk-hints-undo">{tx.t("sketch.hints.undo")}</p>
    </section>
  );
}

/** A small offer on the map ("Fill from roof data", "Add surroundings"), dismissible for this design. */
export function Nudge({ label, style, onClick, onDismiss }: { label: string; style?: CSSProperties; onClick: () => void; onDismiss: () => void }) {
  const tx = useSketchText();
  return (
    <span className="sk-nudge" style={style} onPointerDown={(e) => e.stopPropagation()}>
      <button type="button" className="sk-nudge-go" onClick={onClick}>
        <Sparkles size={13} /> {label}
      </button>
      <button type="button" className="sk-nudge-x" onClick={onDismiss} aria-label={tx.t("sketch.common.notNow")} title={tx.t("sketch.common.notNow")}>
        <X size={12} />
      </button>
    </span>
  );
}
